import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { authApi } from "@/lib/api";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { AuthLayout } from "../components/auth-layout";
import {
  CompactCard,
  CompactField,
  CompactFormActionButton,
  CompactFormFooter,
  CompactFormHeader,
} from "@/components/compact-ui";
import "@/components/compact-ui/compact-form.css";
import { isFormSaveDisabled } from "@/lib/form-save-disabled";

const passwordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, "Mínimo 8 caracteres")
      .regex(/[A-Z]/, "Al menos una mayúscula")
      .regex(/[a-z]/, "Al menos una minúscula")
      .regex(/[0-9]/, "Al menos un número")
      .regex(/[^A-Za-z0-9]/, "Al menos un carácter especial"),
    confirmPassword: z.string().min(1, "Confirma la contraseña"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
  });

type SetPasswordFormData = z.infer<typeof passwordSchema>;

export const SetInitialPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SetPasswordFormData>({
    resolver: zodResolver(passwordSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: { newPassword: "", confirmPassword: "" },
  });

  const newPasswordValue = watch("newPassword");
  const confirmPasswordValue = watch("confirmPassword");

  const saveDisabled = isFormSaveDisabled({
    canSave: !loading && Boolean(token),
    hasRequiredValues:
      Boolean(newPasswordValue) && Boolean(confirmPasswordValue),
    hasRequiredFieldErrors: Boolean(
      errors.newPassword || errors.confirmPassword,
    ),
  });

  useEffect(() => {
    if (!token) {
      setError("Enlace de activación no válido o faltante.");
    }
  }, [token]);

  const onSubmit = async (data: SetPasswordFormData) => {
    setError(null);
    if (!token) return;
    try {
      setLoading(true);
      await authApi.setInitialPassword(token, data.newPassword);
      setSuccess(true);
    } catch (err: unknown) {
      const msg =
        (err as { message?: string }).message ||
        "El enlace de activación es inválido o ha expirado.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout>
        <div className="compact-form">
          <CompactFormHeader badge="Activación completada" />
          <div className="compact-form-body">
            <CompactCard title="¡Bienvenido a WMPartner!">
              <p className="text-[11px] text-muted-foreground">
                Tu contraseña ha sido establecida correctamente. Ya puedes
                iniciar sesión con tu correo electrónico y tu nueva contraseña.
              </p>
            </CompactCard>
          </div>
          <CompactFormFooter>
            <CompactFormActionButton
              variant="primary"
              label="Ir al login"
              hideIcon
              onClick={() => navigate("/login")}
            />
          </CompactFormFooter>
        </div>
      </AuthLayout>
    );
  }

  if (!token) {
    return (
      <AuthLayout>
        <div className="compact-form">
          <CompactFormHeader badge="Enlace inválido" />
          <div className="compact-form-body">
            <CompactCard title="Enlace inválido">
              <p className="text-[11px] text-muted-foreground">
                El enlace de activación no es válido o ha expirado. Contacta a
                tu administrador para que te genere un nuevo enlace.
              </p>
            </CompactCard>
          </div>
          <CompactFormFooter>
            <CompactFormActionButton
              variant="secondary"
              label="Volver al login"
              onClick={() => navigate("/login")}
            />
          </CompactFormFooter>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="compact-form">
        <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
          <CompactFormHeader badge="Activación" />

          <div className="compact-form-body">
            <CompactCard
              title="Establece tu contraseña"
              hint="Bienvenido a WMPartner. Define tu contraseña de acceso."
            >
              <CompactField
                label="Contraseña"
                htmlFor="initial-newPassword"
                required
                error={errors.newPassword?.message}
                hint="Mínimo 8 caracteres con mayúscula, minúscula, número y carácter especial."
              >
                <Input
                  id="initial-newPassword"
                  type="password"
                  placeholder="Ej. MiClave#2024"
                  disabled={loading}
                  autoComplete="new-password"
                  {...register("newPassword")}
                />
              </CompactField>

              <CompactField
                label="Confirmar"
                htmlFor="initial-confirmPassword"
                required
                error={errors.confirmPassword?.message}
              >
                <Input
                  id="initial-confirmPassword"
                  type="password"
                  placeholder="Repite la contraseña"
                  disabled={loading}
                  autoComplete="new-password"
                  {...register("confirmPassword")}
                />
              </CompactField>

              {error ? (
                <p
                  className="compact-span-all text-[11px] text-destructive"
                  role="alert"
                >
                  {error}
                </p>
              ) : null}
            </CompactCard>
          </div>

          <CompactFormFooter
            cancel={{
              label: "Volver al login",
              onClick: () => navigate("/login"),
              disabled: loading,
            }}
            submit={{
              label: "Establecer contraseña",
              loadingLabel: "Estableciendo…",
              disabled: saveDisabled,
              isSubmitting: loading,
            }}
          />
        </form>
      </div>
    </AuthLayout>
  );
};
